import dayjs from 'dayjs';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../dto/data-model.js';
import { BusinessCategory } from './business-category.js';
import { Language } from './language.js';
import { PointOfInterestTag } from '../../components/point-of-interest/point-of-interest-tag-component.js';
import { SubscriptionBadgeType } from './subscription-badge-type.js';
import { MenuType } from './menu/menu-type.js';
import { ICloneable } from '../../contracts/cloneable.js';
import { IDataComparable } from '../../contracts/data-comparable.js';
import {
  cloneList, cloneMap, cloneMapOrNull, convertFromLanguageMapOrNull, isSameData,
  isSameList, isSameMap, mapToData,
} from '../dto/data-model-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import {
  fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString,
  toEnumFromStringOrNull,
} from '../../serialization/enum-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { EventInfo, LocationInfo } from './event-info.js';
import { SerializationException } from '../../exceptions/serialization-exception.js';
import { ArgumentException } from '../../exceptions/argument-exception.js';
import { assertUnreachable } from '../../utility/general-utility.js';

export class DiscoverPageInfo extends DataModel {
  static readonly TYPE_ID: string = '7ad3a409cade4c3188f22d04c7fb4e0f';

  private constructor(
    public readonly uniqueId: string,
    public readonly webRoute: string,
    public readonly isFestival: boolean,
    public readonly referralLink: string | null,
    public readonly images: string[],
    public readonly title: Map<Language, string>,
    public readonly description: Map<Language, string>,
    public readonly location: Map<Language, string>,
    public readonly categories: DiscoverCategoryInfo[],
    public readonly items: DiscoverItemInfo[],
    public readonly seo: DiscoverSeoInfo | null,
  ) {
    super();
  }

  get $typeId(): string {
    return DiscoverPageInfo.TYPE_ID;
  }

  static create(args: {
    uniqueId: string,
    webRoute: string,
    isFestival: boolean,
    referralLink: string | null,
    images: string[],
    title: Map<Language, string>,
    description: Map<Language, string>,
    location: Map<Language, string>,
    categories: DiscoverCategoryInfo[],
    items: DiscoverItemInfo[],
    seo: DiscoverSeoInfo | null,
  }): DiscoverPageInfo {
    return new DiscoverPageInfo(
        args.uniqueId,
        args.webRoute,
        args.isFestival,
        args.referralLink,
        args.images,
        args.title,
        args.description,
        args.location,
        args.categories,
        args.items,
        args.seo,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): DiscoverPageInfo {
    return DiscoverPageInfo.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      webRoute: MapUtils.getString(map, 'webRoute'),
      isFestival: MapUtils.getBoolean(map, 'isFestival'),
      referralLink: MapUtils.getStringOrNull(map, 'referralLink'),
      images: MapUtils.getList(map, 'images', (x) => x as string),
      title: MapUtils.getMap<Language, string>(map, 'title', (k, v) => {
        return {
          key: toEnumFromString(k, Language),
          value: v as string,
        };
      }),
      description: MapUtils.getMap<Language, string>(map, 'description', (k, v) => {
        return {
          key: toEnumFromString(k, Language),
          value: v as string,
        };
      }),
      location: MapUtils.getMap<Language, string>(map, 'location', (k, v) => {
        return {
          key: toEnumFromString(k, Language),
          value: v as string,
        };
      }),
      categories: MapUtils.getList(map, 'categories', (x) => DiscoverCategoryInfo.fromMap(context, x as SerializedData)),
      items: MapUtils.getList(map, 'items', (x) => DiscoverItemInfo.fromMap(context, x as SerializedData)),
      seo: DiscoverSeoInfo.fromMapOrNull(context, MapUtils.getOrNull<SerializedData>(map, 'seo')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      uniqueId: this.uniqueId,
      webRoute: this.webRoute,
      isFestival: this.isFestival,
      referralLink: this.referralLink,
      images: this.images,
      title: mapToData(this.title, (k, v) => {
        return {
          key: fromEnumAsString(k),
          value: v,
        };
      }),
      description: mapToData(this.description, (k, v) => {
        return {
          key: fromEnumAsString(k),
          value: v,
        };
      }),
      location: mapToData(this.location, (k, v) => {
        return {
          key: fromEnumAsString(k),
          value: v,
        };
      }),
      categories: this.categories.map((x) => x.toMap(context)),
      items: this.items.map((x) => x.toMap(context)),
      seo: this.seo?.toMap(context) ?? null,
    };
  }

  clone(): this {
    return DiscoverPageInfo.create({
      uniqueId: this.uniqueId,
      webRoute: this.webRoute,
      isFestival: this.isFestival,
      referralLink: this.referralLink,
      images: cloneList(this.images),
      title: cloneMap(this.title),
      description: cloneMap(this.description),
      location: cloneMap(this.location),
      categories: this.categories.map((x) => x.clone()),
      items: this.items.map((x) => x.clone()),
      seo: this.seo?.clone() ?? null,
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.webRoute === this.webRoute &&
      other.isFestival === this.isFestival &&
      other.referralLink === this.referralLink &&
      isSameList(other.images, this.images) &&
      isSameMap(other.title, this.title) &&
      isSameMap(other.description, this.description) &&
      isSameMap(other.location, this.location) &&
      isSameList(other.categories, this.categories, isSameData) &&
      isSameList(other.items, this.items, isSameData) &&
      isSameData(other.seo, this.seo);
  }
}

export class DiscoverCategoryInfo implements ICloneable, IDataComparable {
  private constructor(
    public readonly uniqueId: string,
    public readonly icon: string | null,
    public readonly name: Map<Language, string>,
    public readonly isFeatured: boolean,
    public readonly rank: number,
    public readonly filterOrder: number,
  ) {}

  static create(args: {
    uniqueId: string,
    icon: string | null,
    name: Map<Language, string>,
    isFeatured: boolean,
    rank: number,
    filterOrder: number,
  }): DiscoverCategoryInfo {
    return new DiscoverCategoryInfo(
        args.uniqueId,
        args.icon,
        args.name,
        args.isFeatured,
        args.rank,
        args.filterOrder,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): DiscoverCategoryInfo {
    return new DiscoverCategoryInfo(
        MapUtils.getString(map, 'uniqueId'),
        MapUtils.getStringOrNull(map, 'icon'),
        MapUtils.getMap<Language, string>(map, 'name', (k, v) => {
          return {
            key: toEnumFromString(k, Language),
            value: v as string,
          };
        }),
        MapUtils.getBoolean(map, 'isFeatured'),
        MapUtils.getNumber(map, 'rank'),
        MapUtils.getNumber(map, 'filterOrder'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      uniqueId: this.uniqueId,
      icon: this.icon,
      name: mapToData(this.name, (k, v) => {
        return {
          key: fromEnumAsString(k),
          value: v,
        };
      }),
      isFeatured: this.isFeatured,
      rank: this.rank,
      filterOrder: this.filterOrder,
    };
  }

  clone(): this {
    return new DiscoverCategoryInfo(
        this.uniqueId,
        this.icon,
        cloneMap(this.name),
        this.isFeatured,
        this.rank,
        this.filterOrder,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return false;
    }

    return other.uniqueId === this.uniqueId &&
      other.icon === this.icon &&
      isSameMap(other.name, this.name) &&
      other.isFeatured === this.isFeatured &&
      other.rank === this.rank &&
      other.filterOrder === this.filterOrder;
  }
}

export enum DiscoverItemType {
  Business = 'BUSINESS',
  Event = 'EVENT',
}

export class DiscoverItemInfo implements ICloneable, IDataComparable {
  private constructor(
    public readonly classification: string[],
    public readonly type: DiscoverItemType,
    public readonly item: BusinessInfo | EventInfo,
  ) {
    switch(type) {
      case DiscoverItemType.Business:
        if(!(item instanceof BusinessInfo)) {
          throw new ArgumentException('item', 'Invalid item type.');
        }
        break;
      case DiscoverItemType.Event:
        if(!(item instanceof EventInfo)) {
          throw new ArgumentException('item', 'Invalid item type.');
        }
        break;
    }
  }

  static create(args: {
    classification: string[],
    item: BusinessInfo | EventInfo,
  }): DiscoverItemInfo {
    let type;
    switch(true) {
      case args.item instanceof BusinessInfo:
        type = DiscoverItemType.Business;
        break;
      case args.item instanceof EventInfo:
        type = DiscoverItemType.Event;
        break;
      default:
        assertUnreachable(args.item);
    }

    return new DiscoverItemInfo(
        args.classification,
        type,
        args.item,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): DiscoverItemInfo {
    const type = toEnumFromString(MapUtils.getString(map, 'type'), DiscoverItemType) as DiscoverItemType;
    let item: BusinessInfo | EventInfo;
    switch(type) {
      case DiscoverItemType.Business:
        item = BusinessInfo.fromMap(context, MapUtils.get<SerializedData>(map, 'item'));
        break;
      case DiscoverItemType.Event:
        item = EventInfo.fromMap(context, MapUtils.get<SerializedData>(map, 'item'));
        break;
      default:
        throw new SerializationException(`Invalid item type: ${type}`);
    }

    return DiscoverItemInfo.create({
      item: item,
      classification: MapUtils.getList(map, 'classification', (x) => x as string),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      classification: this.classification,
      type: fromEnumAsString(this.type),
      item: this.item.toMap(context),
    };
  }

  clone(): this {
    return DiscoverItemInfo.create({
      classification: cloneList(this.classification),
      item: this.item.clone(),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.classification === this.classification &&
      other.type === this.type &&
      isSameData(other.item, this.item);
  }
}

export class BusinessInfo implements ICloneable, IDataComparable {
  private constructor(
    public readonly uniqueId: string,
    public readonly name: Map<Language, string>,
    public readonly cover: Map<Language, string> | null,
    public readonly businessCategory: BusinessCategory | null,
    public readonly locationInfo: LocationInfo | null,
    public readonly label: Map<Language, string> | null,
    public readonly startDate: dayjs.Dayjs | null,
    public readonly isVerified: boolean,
    public readonly tag: PointOfInterestTag | null,
    public readonly badgeType: SubscriptionBadgeType | null,
    public readonly menuType: MenuType | null,
    public readonly intentOverride: string | null,
    public readonly webRoute: string | null,
  ) {}

  static create(args: {
    uniqueId: string,
    name: Map<Language, string>,
    cover: Map<Language, string> | null,
    businessCategory: BusinessCategory | null,
    locationInfo: LocationInfo | null,
    label: Map<Language, string> | null,
    startDate: dayjs.Dayjs | null,
    isVerified: boolean,
    tag: PointOfInterestTag | null,
    badgeType: SubscriptionBadgeType | null,
    menuType: MenuType | null,
    intentOverride: string | null,
    webRoute: string | null,
  }): BusinessInfo {
    return new BusinessInfo(
        args.uniqueId,
        args.name,
        args.cover,
        args.businessCategory,
        args.locationInfo,
        args.label,
        args.startDate,
        args.isVerified,
        args.tag,
        args.badgeType,
        args.menuType,
        args.intentOverride,
        args.webRoute,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): BusinessInfo {
    return BusinessInfo.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      name: MapUtils.getMap<Language, string>(map, 'name', (k, v) => {
        return {
          key: toEnumFromString(k, Language),
          value: v as string,
        };
      }),
      cover: MapUtils.getMapOrNull<Language, string>(map, 'cover', (k, v) => {
        return {
          key: toEnumFromString(k, Language),
          value: v as string,
        };
      }),
      businessCategory: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'businessCategory'), BusinessCategory),
      locationInfo: LocationInfo.fromMapOrNull(context, MapUtils.getOrNull<SerializedData>(map, 'locationInfo')),
      label: MapUtils.getMapOrNull(map, 'label', (k, v) => {
        return {
          key: toEnumFromString(k, Language),
          value: v as string,
        };
      }),
      startDate: TimeZone.decodeDateOrNull(MapUtils.getNumberOrNull(map, 'startDate')),
      isVerified: MapUtils.getBoolean(map, 'isVerified'),
      tag: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'tag'), PointOfInterestTag),
      badgeType: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'badgeType'), SubscriptionBadgeType),
      menuType: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'menuType'), MenuType),
      intentOverride: MapUtils.getStringOrNull(map, 'intentOverride'),
      webRoute: MapUtils.getStringOrNull(map, 'webRoute'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      uniqueId: this.uniqueId,
      name: mapToData(this.name, (k, v) => {
        return {
          key: fromEnumAsString(k),
          value: v,
        };
      }),
      cover: convertFromLanguageMapOrNull(this.cover),
      businessCategory: fromEnumAsStringOrNull(this.businessCategory),
      locationInfo: this.locationInfo?.toMap(context) ?? null,
      label: this.label == null ? null : mapToData(this.label, (k, v) => {
        return {
          key: fromEnumAsString(k),
          value: v,
        };
      }),
      startDate: TimeZone.encodeDateOrNull(this.startDate),
      isVerified: this.isVerified,
      tag: fromEnumAsStringOrNull(this.tag),
      badgeType: fromEnumAsStringOrNull(this.badgeType),
      menuType: fromEnumAsStringOrNull(this.menuType),
      intentOverride: this.intentOverride,
      webRoute: this.webRoute,
    };
  }

  clone(): this {
    return BusinessInfo.create({
      uniqueId: this.uniqueId,
      name: cloneMap(this.name),
      cover: cloneMapOrNull(this.cover),
      businessCategory: this.businessCategory,
      badgeType: this.badgeType,
      intentOverride: this.intentOverride,
      isVerified: this.isVerified,
      label: this.label == null ? null : cloneMap(this.label),
      locationInfo: this.locationInfo?.clone() ?? null,
      menuType: this.menuType,
      startDate: this.startDate,
      tag: this.tag,
      webRoute: this.webRoute,
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      isSameMap(other.name, this.name) &&
      isSameMap(other.cover, this.cover) &&
      other.businessCategory === this.businessCategory &&
      isSameData(other.locationInfo, this.locationInfo) &&
      isSameMap(other.label, this.label) &&
      other.startDate === this.startDate &&
      other.isVerified === this.isVerified &&
      other.tag === this.tag &&
      other.badgeType === this.badgeType &&
      other.menuType === this.menuType &&
      other.intentOverride === this.intentOverride &&
      other.webRoute === this.webRoute;
  }
}

export class DiscoverSeoInfo implements ICloneable, IDataComparable {
  private constructor(
    public readonly title: string,
    public readonly description: string,
    public readonly url: string | null,
    public readonly image: string | null,
  ) {}

  static create(args: {
    title: string,
    description: string,
    url: string | null,
    image: string | null,
  }): DiscoverSeoInfo {
    return new DiscoverSeoInfo(
        args.title,
        args.description,
        args.url,
        args.image
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): DiscoverSeoInfo {
    return DiscoverSeoInfo.create({
      title: MapUtils.getString(map, 'title'),
      description: MapUtils.getString(map, 'description'),
      url: MapUtils.getStringOrNull(map, 'url'),
      image: MapUtils.getStringOrNull(map, 'image'),
    });
  }

  static fromMapOrNull(context: SerializationContext, map: SerializedData | null): DiscoverSeoInfo | null {
    if(map == null) {
      return null;
    }

    return DiscoverSeoInfo.fromMap(context, map);
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      title: this.title,
      description: this.description,
      url: this.url,
      image: this.image,
    };
  }

  clone(): this {
    return DiscoverSeoInfo.create({
      title: this.title,
      description: this.description,
      url: this.url,
      image: this.image,
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.title === this.title &&
      other.description === this.description &&
      other.url === this.url &&
      other.image === this.image;
  }
}

export class DiscoverPageInfoBuilder extends DataModelBuilder<DiscoverPageInfo> {
  build(context: SerializationContext, map: SerializedData): DiscoverPageInfo {
    return DiscoverPageInfo.fromMap(context, map);
  }
}
